import PropTypes from 'prop-types';
import styles from './uploadMediaButton.module.scss';
import { IconButton, Typography } from '@mui/material';
import CloseRoundedIcon from '@mui/icons-material/CloseRounded';
import { useRef } from 'react';
import colors from '../../../utils/colors';
import CircularProgressWithLabel from '../../../components/CreateVideo/CircularProgressWithLabel';
import { isNil } from '../../../utils/general';
import clsx from 'clsx';
import Dropzone from 'react-dropzone';
import { useTranslation } from 'react-i18next';

const UploadMediaButton = ({
    placeholderIconSrc,
    placeholderLabel,
    onCancelClick,
    onChange,
    accept,
    loading,
    loadingValue,
    hasError,
    uploaded,
    uploadedIconSrc,
    uploadedLabel
}) => {
    const { t } = useTranslation();
    const inputRef = useRef(null);

    const onDrop = (acceptedFiles) => {
        if (acceptedFiles.length > 0) {
            const file = acceptedFiles[0];
            onChange({ target: { files: [file] } });
        }
    };

    return (
        <Dropzone onDrop={onDrop} multiple={false} accept={{ [accept]: [] }}>
            {({ getRootProps, getInputProps, isDragAccept }) => (
                <div className={styles.wrapper}>
                    {uploaded && (
                        <IconButton
                            className={styles.cancelButton}
                            sx={{
                                width: '20px',
                                height: '20px',
                                position: 'absolute'
                            }}
                            onClick={(event) => {
                                event.stopPropagation();
                                onCancelClick?.();
                            }}>
                            <CloseRoundedIcon
                                sx={{
                                    width: '16px',
                                    height: '16px',
                                    fill: 'white',
                                    strokeWidth: 2
                                }}
                            />
                        </IconButton>
                    )}
                    <div
                        className={clsx(
                            styles.root,
                            styles.activeRoot,
                            hasError && styles.errorRoot,
                            isDragAccept && styles.dragAccept
                        )}
                        {...getRootProps()}>
                        {!loading && !uploaded && (
                            <div className={styles.placeholderContainer}>
                                {!!placeholderIconSrc && (
                                    <img
                                        src={placeholderIconSrc}
                                        className={styles.icon}
                                        aria-hidden={true}
                                    />
                                )}
                                <Typography fontSize="11px" lineHeight="16px">
                                    {placeholderLabel}
                                </Typography>
                            </div>
                        )}
                        {!loading && uploaded && (
                            <div className={styles.placeholderContainer}>
                                {!!uploadedIconSrc && (
                                    <img
                                        src={uploadedIconSrc}
                                        className={styles.icon}
                                        aria-hidden={true}
                                    />
                                )}
                                <Typography
                                    fontSize="10px"
                                    lineHeight="12px"
                                    fontFamily="InstrumentSans-semibold">
                                    {uploadedLabel}
                                </Typography>
                                <Typography
                                    fontSize="10px"
                                    lineHeight="12px"
                                    fontFamily="InstrumentSans-semibold"
                                    sx={{ opacity: 0.7 }}>
                                    {t('Uploaded')}
                                </Typography>
                            </div>
                        )}
                        <input
                            key={`fileInput-${uploaded}-${loading}`}
                            type="file"
                            accept={accept}
                            onChange={onChange}
                            style={{ display: 'none' }}
                            ref={inputRef}
                            {...getInputProps()}
                        />
                        {loading && (
                            <div className={styles.progressContainer}>
                                <CircularProgressWithLabel
                                    size={30}
                                    color={colors.primary}
                                    showValue={false}
                                    thickness={10}
                                    variant={!loadingValue ? 'indeterminate' : 'determinate'}
                                    value={isNil(loadingValue) ? 100 : loadingValue}
                                />
                            </div>
                        )}
                    </div>
                </div>
            )}
        </Dropzone>
    );
};

UploadMediaButton.propTypes = {
    placeholderIconSrc: PropTypes.string,
    placeholderLabel: PropTypes.string,
    onCancelClick: PropTypes.func,
    onChange: PropTypes.func,
    accept: PropTypes.string,
    loading: PropTypes.bool,
    loadingValue: PropTypes.number,
    hasError: PropTypes.bool,
    uploaded: PropTypes.bool,
    uploadedIconSrc: PropTypes.string,
    uploadedLabel: PropTypes.string
};

export default UploadMediaButton;
